import { VariablesOf } from '@graphql-typed-document-node/core';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { EVENTS_PER_PAGE_COLLECTION } from 'constants/events.constant';
import { datoRequester } from 'queries/requesters';
import { StaleTime } from 'queries/types';
import {
  CityEventsDocument,
  PageCityDocument,
} from 'types/api/dato-operations.types';

import { sortByEndDateTime } from 'components/@events/utils';

import { CityToEventsTotal } from './utils';

const CITIES_PREFIX = 'cities';

export const pageCityQuery = (
  variables: VariablesOf<typeof PageCityDocument>,
) =>
  queryOptions({
    queryKey: [CITIES_PREFIX, 'detail', variables],
    queryFn: datoRequester(PageCityDocument, variables),
  });

export const cityEventsQuery = (
  variables: VariablesOf<typeof CityEventsDocument>,
  eventsPerPage: number = EVENTS_PER_PAGE_COLLECTION,
) => {
  //Removed startOfDay because date server/Azie was not same as on client -> Server/Client hydration mismatch due to different querykeys
  const { currentISOString, ...rest } = variables;
  return infiniteQueryOptions({
    queryKey: [CITIES_PREFIX, 'events', rest],
    queryFn: async (ctx) => {
      const { pageCity } = await datoRequester(CityEventsDocument, {
        ...variables,
        skip: ctx.pageParam,
      })(ctx);

      const unsortedEvents =
        pageCity?.venues.flatMap((venue) => venue.events) ?? [];

      const events = unsortedEvents?.sort(sortByEndDateTime);

      const cityEventsTotal = pageCity?.venues.map(CityToEventsTotal) ?? [];
      const totalUpcomingEvents = cityEventsTotal.reduce(
        (total, current) => total + current.upcomingEvents,
        0,
      );

      const meta = totalUpcomingEvents;

      return { events, meta };
    },

    staleTime: StaleTime.ONE_HOUR,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoadedCount = allPages.reduce(
        (acc, item) => acc + item.events.length,
        0,
      );

      const totalCount = lastPage.meta;

      if (totalCount) {
        return totalLoadedCount < totalCount
          ? allPages.length * eventsPerPage
          : undefined;
      }

      return 0;
    },
  });
};
